// Live next-month engineer-month projection: budget vs. every demand the engine
// will actually draw, WITH the player's queued orders applied. Mirrors the
// engine's consumption rules (projects.ts / nodeAccess.ts) — if those change,
// this must change with them:
//   design:  NRE → sustaining tax (0.5/product) → feasibility·design·layout-support·respinFix staffing
//   verif:   design · waitingTapein · layout · waitingTapeout staffing
//   impl:    node qual (reserved) → yield research → reliability → design·layout staffing,
//            prodQual draws exactly 2 (only if staffing.implementation ≥ 2)
//   all:     in-house tool programs draw staff EM ahead of projects (tools.ts)
// NOTE: this projects EM *consumed* (payroll); the Brooks-law curve means EM
// *banked* by an overstaffed project is less — see idealStaffing/effectiveHeads.

import type { Command, ContentPack, Discipline, PlayerView } from "../engine/types.js";

export interface DisciplineProjection {
  budget: number; // productive + rampEffectiveness × ramping
  nre: number;
  sustaining: number;
  qual: number;
  research: number;
  reliability: number;
  tools: number;
  tree: number;
  projects: number;
  committed: number;
  idle: number; // negative = oversubscribed
}

export type AllocationProjection = Record<Discipline, DisciplineProjection>;

export function projectNextMonth(
  view: PlayerView,
  pending: readonly Command[],
  content: ContentPack,
): AllocationProjection {
  const eco = content.economy;
  const models = eco.models;

  // ---- queued-order overrides (queue is replace-keyed: ≤1 per key) ----
  const pendingNre = pending.find((c) => c.type === "setNre");
  const staffingOf = (projectId: string, current: Record<Discipline, number>) => {
    const c = pending.find((x) => x.type === "setStaffing" && x.projectId === projectId);
    return c && c.type === "setStaffing" ? c.staffing : current;
  };
  const researchStaffOf = (nodeId: string, current: number) => {
    const c = pending.find((x) => x.type === "setYieldResearch" && x.nodeId === nodeId);
    return c && c.type === "setYieldResearch" ? c.engineers : current;
  };
  const reliabilityStaffOf = (nodeId: string, current: number) => {
    const c = pending.find((x) => x.type === "setReliability" && x.nodeId === nodeId);
    return c && c.type === "setReliability" ? c.engineers : current;
  };
  const toolStaffOf = (d: Discipline, current: number) => {
    const c = pending.find((x) => x.type === "setToolStaff" && x.discipline === d);
    return c && c.type === "setToolStaff" ? c.engineers : current;
  };
  const decisionOf = (projectId: string): "tapeIn" | "tapeOut" | "respin" | "cancelProject" | null => {
    const c = pending.find(
      (x) =>
        (x.type === "tapeIn" || x.type === "tapeOut" || x.type === "respin" || x.type === "cancelProject") &&
        x.projectId === projectId,
    );
    return c ? (c.type as "tapeIn" | "tapeOut" | "respin" | "cancelProject") : null;
  };
  const eolQueued = new Set(
    pending.filter((c) => c.type === "endOfLifeProduct").map((c) => (c.type === "endOfLifeProduct" ? c.productId : "")),
  );

  const out: AllocationProjection = {
    design: empty(),
    verification: empty(),
    implementation: empty(),
  };

  for (const d of ["design", "verification", "implementation"] as const) {
    const pool = view.people.pools[d];
    out[d].budget = pool.productive + eco.rampEffectiveness * pool.ramping.reduce((a, r) => a + r.count, 0);
    out[d].tools = toolStaffOf(d, view.tools.inhouse[d].staff);
  }

  // NRE bills design first
  out.design.nre = pendingNre && pendingNre.type === "setNre" ? pendingNre.engineers : view.nreEngineers;

  // sustaining tax: active products (minus queued EOLs)
  const activeProducts = view.products.filter((p) => p.active && !eolQueued.has(p.id)).length;
  out.design.sustaining = activeProducts * models.sustainingEmPerProduct;

  // tree research (P0/P1): staffed nodes draw EM by discipline mix ahead of
  // projects — but only when the node is live (world date + prereqs met);
  // otherwise the engine leaves the staff idle and they cost nothing here
  const tree = content.trees.find((t) => t.id === content.scenario.treeId);
  if (tree) {
    const staffed = new Map<string, number>(Object.entries(view.tree.staffing));
    for (const c of pending) {
      if (c.type === "setTreeStaff") staffed.set(c.nodeId, c.engineers);
    }
    for (const [nodeId, staff] of staffed) {
      if (staff <= 0) continue;
      const node = tree.nodes.find((n) => n.id === nodeId);
      if (!node || nodeId in view.tree.researched) continue;
      if (node.availableMonth > view.monthIndex) continue;
      if (!node.prereqs.every((p) => p in view.tree.researched)) continue;
      for (const d of ["design", "verification", "implementation"] as const) {
        out[d].tree += staff * (node.disciplineMix[d] ?? 0);
      }
    }
  }

  // node programs (implementation, reserved ahead of projects)
  for (const node of content.nodes) {
    const access = view.nodes[node.id]!;
    if (access.status === "qualifying") {
      out.implementation.qual += access.qualEmRemaining / Math.max(1, access.qualMonthsRemaining);
    }
    if (access.status === "locked" && pending.some((c) => c.type === "startNodeQual" && c.nodeId === node.id)) {
      out.implementation.qual += node.qual.engMonths / node.qual.durationMonths;
    }
    if (access.status === "qualified") {
      if (access.researchEm < 5 * models.yieldGapTauEm) {
        out.implementation.research += researchStaffOf(node.id, access.researchStaff);
      }
      if (access.reliabilityEm < 5 * models.reliabilityTauEm) {
        out.implementation.reliability += reliabilityStaffOf(node.id, access.reliabilityStaff);
      }
    }
  }

  // per-project draws, phase-aware (with queued decisions applied)
  for (const p of view.projects) {
    let phase: string = p.phase;
    const decision = decisionOf(p.id);
    if (decision === "cancelProject") continue;
    if (decision === "tapeIn" && phase === "waitingTapein") phase = "layout";
    if (decision === "tapeOut" && phase === "waitingTapeout") phase = "fab";
    if (decision === "respin" && phase === "respinDecision") phase = "respinFix";
    const staffing = staffingOf(p.id, p.staffing);
    switch (phase) {
      case "feasibility":
      case "respinFix":
        out.design.projects += staffing.design;
        break;
      case "design":
      case "layout":
        out.design.projects += staffing.design;
        out.verification.projects += staffing.verification;
        out.implementation.projects += staffing.implementation;
        break;
      case "waitingTapein":
        out.verification.projects += staffing.verification;
        out.implementation.projects += staffing.implementation;
        break;
      case "waitingTapeout":
        out.verification.projects += staffing.verification;
        break;
      case "prodQual":
        // the engine draws exactly prodQualMinImpl, and only when staffed ≥ that
        out.implementation.projects += staffing.implementation >= models.prodQualMinImpl ? models.prodQualMinImpl : 0;
        break;
      default:
        break; // fab, validation, respinDecision, shipped, cancelled: no draw
    }
  }

  for (const d of ["design", "verification", "implementation"] as const) {
    const x = out[d];
    x.committed = x.nre + x.sustaining + x.qual + x.research + x.reliability + x.tools + x.tree + x.projects;
    x.idle = x.budget - x.committed;
  }
  return out;
}

function empty(): DisciplineProjection {
  return { budget: 0, nre: 0, sustaining: 0, qual: 0, research: 0, reliability: 0, tools: 0, tree: 0, projects: 0, committed: 0, idle: 0 };
}
